import { Component } from '@theme/component';

/**
 * Heta Hyttan: kampanjnedräkning. Räknar ner till data-slut (unixtid) och visar eller döljer
 * fältet när kampanjen börjar och slutar, eftersom sidan kan ha cachats före den tidpunkten.
 * I temaredigeraren (data-redigering) visas fältet alltid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [dagEnhet] - Dagar inklusive enhet, döljs under sista dygnet.
 * @property {HTMLElement} [dagar]
 * @property {HTMLElement} [timmar]
 * @property {HTMLElement} [minuter]
 * @property {HTMLElement} [sekunder]
 * @property {HTMLButtonElement} [kod] - Knapp som kopierar rabattkoden.
 * @property {HTMLElement} [kopierad] - Bekräftelse som visas över knappen.
 * @property {HTMLElement} [status] - Skärmläsarstatus efter kopiering.
 *
 * @extends {Component<Refs>}
 */
class NedrakningComponent extends Component {
  /** @type {number | undefined} */
  #timer;

  /** @type {number | undefined} */
  #kopieradTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#uppdatera();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    clearTimeout(this.#kopieradTimer);
  }

  #uppdatera = () => {
    clearTimeout(this.#timer);

    const slut = Number(this.dataset.slut) * 1000;
    if (!slut) return;

    const start = Number(this.dataset.start) * 1000 || 0;
    const nu = Date.now();
    const doljEfter = this.hasAttribute('data-dolj-efter');

    /** @type {'kommande' | 'aktiv' | 'slut'} */
    let status = 'aktiv';
    if (nu < start) status = 'kommande';
    else if (nu >= slut) status = 'slut';

    const synlig = status === 'aktiv' || (status === 'slut' && !doljEfter);
    this.dataset.status = status;
    this.dataset.visning = status === 'slut' && !doljEfter ? 'slut' : 'aktiv';
    this.toggleAttribute('data-synlig', synlig);
    if (!this.hasAttribute('data-redigering')) this.hidden = !synlig;

    if (status === 'slut') {
      this.#visaTid(0);
      return;
    }

    if (status === 'kommande') {
      this.#visaTid(slut - start);
      this.#timer = window.setTimeout(this.#uppdatera, Math.min(start - nu, 2 ** 31 - 1));
      return;
    }

    const kvar = slut - nu;
    this.#visaTid(kvar);
    // Nästa uppdatering precis när sekunden slår om
    this.#timer = window.setTimeout(this.#uppdatera, kvar % 1000 || 1000);
  };

  /** @param {number} millisekunder */
  #visaTid(millisekunder) {
    const totalt = Math.ceil(Math.max(0, millisekunder) / 1000);
    const dagar = Math.floor(totalt / 86400);
    const tvaSiffror = (/** @type {number} */ tal) => String(tal).padStart(2, '0');

    this.#satt(this.refs.dagar, String(dagar));
    this.#satt(this.refs.timmar, tvaSiffror(Math.floor((totalt % 86400) / 3600)));
    this.#satt(this.refs.minuter, tvaSiffror(Math.floor((totalt % 3600) / 60)));
    this.#satt(this.refs.sekunder, tvaSiffror(totalt % 60));
    if (this.refs.dagEnhet) this.refs.dagEnhet.hidden = dagar === 0;
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {string} text
   */
  #satt(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }

  async kopiera() {
    const knapp = this.refs.kod;
    const kod = knapp?.dataset.kod;
    if (!knapp || !kod) return;

    if (!(await this.#kopieraText(kod))) {
      // Kopieringen nekades: markera koden så att den kan kopieras manuellt
      const text = knapp.querySelector('strong');
      if (text) window.getSelection()?.selectAllChildren(text);
      return;
    }

    knapp.setAttribute('data-kopierad', '');
    if (this.refs.status) this.refs.status.textContent = this.refs.kopierad?.textContent ?? '';

    clearTimeout(this.#kopieradTimer);
    this.#kopieradTimer = window.setTimeout(() => {
      knapp.removeAttribute('data-kopierad');
      if (this.refs.status) this.refs.status.textContent = '';
    }, 2000);
  }

  /**
   * Inbyggda webbläsare (t.ex. i Instagram och Facebook) nekar ofta Clipboard API,
   * så då används det äldre execCommand-sättet.
   *
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  async #kopieraText(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const falt = document.createElement('textarea');
      falt.value = text;
      falt.setAttribute('readonly', '');
      falt.style.cssText = 'position:fixed;inset-block-start:0;opacity:0;pointer-events:none';
      this.append(falt);
      falt.select();
      let kopierat = false;
      try {
        kopierat = document.execCommand('copy');
      } catch {
        kopierat = false;
      }
      falt.remove();
      this.refs.kod?.focus();
      return kopierat;
    }
  }
}

if (!customElements.get('hh-nedrakning')) {
  customElements.define('hh-nedrakning', NedrakningComponent);
}
